import { useFacility } from '@/contexts/facility-context/hook';
import { useSet } from '@mantine/hooks';
import { useNavigate } from '@tanstack/react-router';
import React, { createContext, ReactNode, useCallback, useState } from 'react';
import { AssetType, MaterialType } from '../../../../../../backend/src/types.generated';
import { useProcessBalesMutation, useSearchBalesForProcessedLazyQuery } from './graphql.generated';

// Define the shape of your context state
export interface BaleProcessedContextValue {
	page: BaleProcessedPageType;
	setPage: React.Dispatch<React.SetStateAction<BaleProcessedPageType>>;
	handleScan: (code: string) => void;
	showManualCodeEntry: boolean;
	setShowManualCodeEntry: React.Dispatch<React.SetStateAction<boolean>>;
	scannedBales: {
		id: string;
		scannableCode: string;
		materialType: MaterialType;
		assetType: AssetType;
	}[];
	isProcessingBales: boolean;
	submitBales: () => Promise<void>;
}

export type BaleProcessedPageType = 'SCAN' | 'CONFIRM';

// Create the context with undefined as default
// eslint-disable-next-line react-refresh/only-export-components
export const BaleProcessedContext = createContext<BaleProcessedContextValue | undefined>(undefined);

// Provider component props
interface BaleProcessedContextProviderProps {
	children: ReactNode;
}

// Provider component
export const BaleProcessedContextProvider: React.FC<BaleProcessedContextProviderProps> = ({
	children,
}) => {
	const [page, setPage] = useState<BaleProcessedPageType>('SCAN');
	const { currentFacility } = useFacility();
	const ignoredCodes = useSet();
	const [showManualCodeEntry, setShowManualCodeEntry] = useState(false);
	const [getTrackableAssets] = useSearchBalesForProcessedLazyQuery();
	const navigate = useNavigate();
	const [processBales, { loading: isProcessingBales }] = useProcessBalesMutation({
		onCompleted: () => {
			navigate({ to: '/facility/home' });
		},
	});
	const [scannedBales, setScannedBales] = useState<
		{ id: string; scannableCode: string; materialType: MaterialType; assetType: AssetType }[]
	>([]);

	const handleScan = async (code: string) => {
		try {
			if (ignoredCodes.has(code)) {
				return;
			}
			ignoredCodes.add(code);
			const data = await getTrackableAssets({
				variables: { filter: { scannableCode: code.split('/').pop() } },
			}).then((res) => {
				if (res.data?.trackableAssets?.[0]) {
					return res.data.trackableAssets[0];
				}
				return null;
			});
			if (data && data.assetType && data.intendedMaterial && data.status) {
				setScannedBales((prev) => [
					...prev,
					{
						id: data.id,
						scannableCode: code,
						assetType: data.assetType as AssetType,
						materialType: data.intendedMaterial as MaterialType,
					},
				]);
				return {
					status: 'FOUND',
				};
			} else {
				// eslint-disable-next-line no-console
				console.error('Code is in invalid state, cannot evaluate: ', data);
				return {
					status: 'INVALID',
				};
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			return {
				status: 'ERROR',
			};
		}
	};

	const submitBales = useCallback(async () => {
		if (scannedBales.length > 0 && currentFacility) {
			await processBales({
				variables: {
					input: {
						codes: scannedBales.map((bale) => bale.scannableCode),
						facilityId: currentFacility.id,
					},
				},
			});
		}
	}, [scannedBales, processBales, currentFacility]);

	return (
		<BaleProcessedContext.Provider
			value={{
				page,
				setPage,
				handleScan,
				showManualCodeEntry,
				setShowManualCodeEntry,
				scannedBales,
				isProcessingBales,
				submitBales,
			}}
		>
			{children}
		</BaleProcessedContext.Provider>
	);
};
